import { FC, useCallback, useMemo, useState } from 'react';

import { Alert, Modal, Button as PLButton } from '@payaca/ambrosia';
import Button from '@payaca/components/button/Button';
import { ButtonStyleVariant } from '@payaca/components/button/enums';
import PaymentScheduleSummary from '@payaca/components/paymentScheduleSummary/PaymentScheduleSummary';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import {
  getPaymentScheduleFieldValidator,
  isPaymentScheduleValid,
} from '@payaca/helpers/paymentScheduleHelper';
import { useQueryClient } from '@tanstack/react-query';

import useUpdateChangeProposalPaymentSchedule from '@/api/mutations/proposals/useUpdateChangeProposalPaymentSchedule';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import useGetChangeProposal from '@/api/queries/proposal/useGetChangeProposal';
import CreatePaymentScheduleControl, {
  PaymentScheduleInput,
} from '../createEditProposalTemplateControl/CreatePaymentScheduleControl';
import EditJobSection from '../editJob/EditJobSection';

type Props = {
  changeProposalId: string;
  futureProjectValue: number;
};

const ChangeProposalPaymentScheduleSection: FC<Props> = (props) => {
  const { changeProposalId, futureProjectValue } = props;
  const [showEditModal, setShowEditModal] = useState(false);
  const queryClient = useQueryClient();

  const { data } = useGetChangeProposal(changeProposalId);
  const paymentSchedule = data?.changeProposal?.paymentSchedule;

  // Reshape GQL sync stages back into a PaymentSchedule for display
  // When in sync: show all stages as the backend provides them
  // When out of sync: only show stages that have an invoice
  const paymentScheduleForSummary = useMemo(() => {
    if (!paymentSchedule) return null;

    if (paymentSchedule.isInSync) {
      return {
        stages: paymentSchedule.stages
          .filter((s) => s.stage)
          .map((s) => ({
            description: s.stage!.description,
            percentageDue: s.stage!.percentageDue,
            isDeposit: s.stage!.isDeposit,
          })),
        explainer: paymentSchedule.explainer,
      };
    }

    return {
      stages: paymentSchedule.stages
        .filter((s) => s.stage && s.invoice)
        .map((s) => ({
          description: s.stage!.description,
          percentageDue: s.invoiceProjectPercentage ?? 0,
          // Locked (invoice-backed) rows must reflect invoice reality, not the
          // saved schedule — see P-7892.
          isDeposit: s.invoice!.isDepositInvoice,
        })),
      explainer: paymentSchedule.explainer,
    };
  }, [paymentSchedule]);

  // Identify which stages are locked in the edit modal
  const lockedStageIndices = useMemo(() => {
    if (!paymentSchedule) return new Set<number>();

    const indices: number[] = [];
    let displayIndex = 0;
    for (const s of paymentSchedule.stages) {
      // Only count stages that are displayed in the summary/edit modal
      const isDisplayed = paymentSchedule.isInSync
        ? !!s.stage
        : !!s.stage && s.invoice != null;
      if (!isDisplayed) continue;
      if (s.invoice != null) {
        indices.push(displayIndex);
      }
      displayIndex++;
    }

    return new Set(indices);
  }, [paymentSchedule]);

  if (!paymentSchedule) return null;

  return (
    <>
      <EditJobSection
        title={
          <div className="flex flex-row items-baseline justify-between">
            <h2>Payment schedule</h2>
            <Button
              styleVariant={ButtonStyleVariant.ANCHOR}
              onClick={() => setShowEditModal(true)}
            >
              {paymentSchedule.isInSync
                ? 'Edit payment schedule'
                : 'Update payment schedule'}
            </Button>
          </div>
        }
      >
        {!paymentSchedule.isInSync ? (
          <Alert colour="yellow">
            The payment schedule is out of sync with the change proposal value.
            Please update or discard it before sending.
          </Alert>
        ) : (
          <PaymentScheduleSummary
            paymentSchedule={paymentScheduleForSummary}
            projectValue={futureProjectValue}
          />
        )}
      </EditJobSection>
      <Modal
        size="lg"
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit payment schedule"
      >
        {paymentScheduleForSummary && (
          <EditChangeProposalPaymentScheduleModalBody
            changeProposalId={changeProposalId}
            futureProjectValue={futureProjectValue}
            isInSync={paymentSchedule.isInSync}
            lockedStageIndices={lockedStageIndices}
            paymentSchedule={paymentScheduleForSummary}
            onSuccess={() => {
              setShowEditModal(false);
              void queryClient.invalidateQueries({
                queryKey: proposalKeys.changeProposal(changeProposalId),
              });
            }}
          />
        )}
      </Modal>
    </>
  );
};

const EditChangeProposalPaymentScheduleModalBody: FC<{
  changeProposalId: string;
  futureProjectValue: number;
  isInSync: boolean;
  lockedStageIndices: Set<number>;
  paymentSchedule: PaymentScheduleInput;
  onSuccess?: () => void;
}> = (props) => {
  const {
    changeProposalId,
    futureProjectValue,
    isInSync,
    lockedStageIndices,
    paymentSchedule,
    onSuccess,
  } = props;

  const { mutateAsync: mutateUpdatePaymentSchedule, isPending: isUpdating } =
    useUpdateChangeProposalPaymentSchedule({
      onSuccess,
    });

  const onDelete = useCallback(() => {
    void mutateUpdatePaymentSchedule({
      changeProposalId,
      paymentSchedule: null,
    });
  }, [changeProposalId, mutateUpdatePaymentSchedule]);

  const initialFormState = useMemo(() => {
    return { paymentSchedule };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only using initial value
  }, []);

  const fieldValidators = useMemo(() => {
    return {
      paymentSchedule: [getPaymentScheduleFieldValidator()],
    };
  }, []);

  const onSubmit = useCallback(
    (schedule: PaymentScheduleInput) => {
      const validStages = (schedule?.stages || [])
        .map((x) => ({ ...x, percentageDue: +x.percentageDue }))
        .filter((stage) => stage.description && stage.percentageDue);

      const cleanedPaymentSchedule = {
        explainer: validStages?.length ? schedule?.explainer : null,
        stages: validStages,
      };

      if (!isPaymentScheduleValid(cleanedPaymentSchedule)) return;

      void mutateUpdatePaymentSchedule({
        changeProposalId,
        paymentSchedule: cleanedPaymentSchedule,
      });
    },
    [changeProposalId, mutateUpdatePaymentSchedule]
  );

  return (
    <ValidatedForm<{ paymentSchedule: PaymentScheduleInput }>
      fieldValidators={fieldValidators}
      initialFormState={initialFormState}
      renderFormContents={(
        isValid,
        formState,
        validationState,
        _touchedState,
        onFieldChange
      ) => {
        return (
          <>
            <Modal.Body>
              {!isInSync && (
                <Alert colour="yellow" className="mb-4">
                  Disabled rows represent stages that have already been
                  invoiced.
                </Alert>
              )}
              <CreatePaymentScheduleControl
                predictedProjectValue={futureProjectValue}
                lockedStageIndices={lockedStageIndices}
                paymentSchedule={formState.paymentSchedule}
                onChange={(ps) => onFieldChange({ paymentSchedule: ps })}
                fieldValidationState={
                  isValid
                    ? undefined
                    : {
                        isValid: false,
                        validationMessages:
                          validationState.paymentSchedule?.errors,
                      }
                }
              />
            </Modal.Body>
            <Modal.Footer>
              <Modal.Footer.Actions>
                <PLButton
                  isProcessing={isUpdating}
                  onClick={() => onDelete()}
                  variant={'outline'}
                  colour={'red'}
                >
                  Delete
                </PLButton>
                <PLButton
                  isProcessing={isUpdating}
                  disabled={!isValid}
                  onClick={() => onSubmit(formState.paymentSchedule)}
                >
                  Save
                </PLButton>
              </Modal.Footer.Actions>
            </Modal.Footer>
          </>
        );
      }}
    />
  );
};

export default ChangeProposalPaymentScheduleSection;
